(function () {
  'use strict';

  if (window.BKCollectionEnhancement) {
    window.BKCollectionEnhancement.scan();
    return;
  }

  const instances = new Map();
  let sheetLockOwner = null;

  class BKCollection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.enabled = root.dataset.enableAjax === 'true';
      this.controller = null;
      this.requestSequence = 0;
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onOrientationChange = this.onOrientationChange.bind(this);
      this.openSheet = null;
      this.sheetReturnFocus = null;
      this.sheetScrollPosition = null;
      this.sheetScrollRestoreTimer = null;
      this.backdrop = this.root.querySelector('.bk-v29-collection__sheet-backdrop');
      this.bind();
      this.syncSheetAccessibility();
      requestAnimationFrame(() => this.revealActiveCategory());
    }

    bind() {
      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('submit', this.onSubmit);
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onOrientationChange, { passive: true });
    }

    destroy() {
      this.controller?.abort();
      window.clearTimeout(this.sheetScrollRestoreTimer);
      this.closeFilterSheet({ restoreFocus: false });
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('submit', this.onSubmit);
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onOrientationChange);
    }

    isPlainPrimaryClick(event) {
      return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    }

    onClick(event) {
      const sheetOpen = event.target.closest('[data-bk-sheet-open]');
      if (sheetOpen && this.root.contains(sheetOpen)) {
        event.preventDefault();
        this.openFilterSheet(sheetOpen.dataset.bkSheetOpen, sheetOpen);
        return;
      }

      const sheetClose = event.target.closest('[data-bk-sheet-close]');
      if (sheetClose && this.root.contains(sheetClose)) {
        event.preventDefault();
        this.closeFilterSheet();
        return;
      }

      const concernToggle = event.target.closest('[data-bk-concern-toggle]');
      if (concernToggle && this.root.contains(concernToggle) && this.isPlainPrimaryClick(event)) {
        event.preventDefault();
        this.toggleConcernNavigation(concernToggle);
        return;
      }

      if (!this.enabled || !this.isPlainPrimaryClick(event)) return;
      const link = event.target.closest('[data-bk-ajax-link]');
      if (!link || !this.root.contains(link) || link.target === '_blank' || link.hasAttribute('download')) return;

      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;

      event.preventDefault();
      this.closeFilterSheet({ restoreFocus: false });
      this.navigate(url.href, { push: true, focusResults: true });
    }

    onSubmit(event) {
      const form = event.target.closest('[data-bk-filter-form]');
      if (!this.enabled || !form || !this.root.contains(form)) return;

      event.preventDefault();
      this.closeFilterSheet({ restoreFocus: false });
      const url = new URL(form.action, window.location.origin);
      const params = new URLSearchParams(new FormData(form));
      params.forEach((value, key) => {
        if (value !== '') url.searchParams.append(key, value);
      });
      this.navigate(url.href, { push: true, focusResults: true });
    }

    onChange(event) {
      const form = event.target.closest('[data-bk-filter-form]');
      if (!this.enabled || !form) return;

      if (event.target.matches('[data-bk-sort], input[type="checkbox"]') && window.innerWidth >= 750) {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      }
    }

    onKeydown(event) {
      if (this.openSheet && event.key === 'Tab') {
        const focusable = Array.from(this.openSheet.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), summary'))
          .filter((element) => element.offsetParent !== null);
        if (focusable.length) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.openSheet) {
        event.preventDefault();
        this.closeFilterSheet();
        return;
      }
      const toggle = this.root.querySelector('[data-bk-concern-toggle][aria-expanded="true"]');
      if (!toggle) return;
      event.preventDefault();
      this.toggleConcernNavigation(toggle, false);
      toggle.focus();
    }

    toggleConcernNavigation(toggle, forceOpen) {
      const rowId = toggle.getAttribute('aria-controls');
      const row = rowId ? this.root.querySelector(`#${CSS.escape(rowId)}`) : null;
      if (!row) return;
      const open = typeof forceOpen === 'boolean' ? forceOpen : toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.classList.toggle('is-expanded', open);
      row.hidden = !open;
      row.setAttribute('aria-hidden', String(!open));
      const sign = toggle.querySelector('[data-bk-concern-sign]');
      sign?.classList.toggle('is-open', open);
    }

    revealActiveCategory() {
      const row = this.root.querySelector('.bk-v29-collection__primary-row');
      const active = row?.querySelector('.bk-v29-collection__category.is-active');
      if (!row || !active || row.scrollWidth <= row.clientWidth) return;
      active.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    }

    openFilterSheet(name, trigger) {
      if (window.innerWidth >= 750) return;
      const sheet = this.root.querySelector(`[data-bk-sheet="${CSS.escape(name || '')}"]`);
      if (!sheet) return;
      const scrollPosition = this.sheetScrollPosition || { x: window.scrollX, y: window.scrollY };
      if (sheetLockOwner && sheetLockOwner !== this) sheetLockOwner.closeFilterSheet({ restoreFocus: false });
      if (this.openSheet && this.openSheet !== sheet) this.closeFilterSheet({ restoreFocus: false });
      this.sheetScrollPosition = scrollPosition;
      sheetLockOwner = this;
      this.openSheet = sheet;
      this.sheetReturnFocus = trigger;
      sheet.dataset.open = 'true';
      sheet.hidden = false;
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      sheet.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      this.root.classList.add('is-sheet-open');
      if (this.backdrop) this.backdrop.hidden = false;
      requestAnimationFrame(() => {
        (sheet.querySelector('[data-bk-sheet-close]') || sheet).focus({ preventScroll: true });
        this.restoreSheetScroll();
      });
    }

    closeFilterSheet({ restoreFocus = true } = {}) {
      if (!this.openSheet) {
        if (sheetLockOwner === this) {
          this.root.classList.remove('is-sheet-open');
          if (this.backdrop) this.backdrop.hidden = true;
          sheetLockOwner = null;
        }
        return;
      }
      const sheet = this.openSheet;
      delete sheet.dataset.open;
      sheet.hidden = true;
      sheet.removeAttribute('aria-modal');
      sheet.setAttribute('aria-hidden', 'true');
      this.root.querySelectorAll('[data-bk-sheet-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.root.classList.remove('is-sheet-open');
      if (this.backdrop) this.backdrop.hidden = true;
      if (sheetLockOwner === this) sheetLockOwner = null;
      this.openSheet = null;
      if (restoreFocus && this.sheetReturnFocus && document.contains(this.sheetReturnFocus)) {
        this.sheetReturnFocus.focus({ preventScroll: true });
      }
      this.sheetReturnFocus = null;
      this.restoreSheetScroll({ clear: true });
    }

    restoreSheetScroll({ clear = false } = {}) {
      const position = this.sheetScrollPosition;
      if (!position) return;
      const restore = () => window.scrollTo(position.x, position.y);
      requestAnimationFrame(restore);
      window.clearTimeout(this.sheetScrollRestoreTimer);
      this.sheetScrollRestoreTimer = window.setTimeout(restore, 260);
      if (clear) this.sheetScrollPosition = null;
    }

    onResize() {
      if (window.innerWidth >= 750 && this.openSheet) this.closeFilterSheet({ restoreFocus: false });
      this.syncSheetAccessibility();
    }

    onOrientationChange() {
      if (this.openSheet) this.closeFilterSheet({ restoreFocus: false });
      this.syncSheetAccessibility();
    }

    syncSheetAccessibility() {
      const mobile = window.innerWidth < 750;
      this.root.querySelectorAll('[data-bk-sheet]').forEach((sheet) => {
        sheet.setAttribute('aria-hidden', mobile && sheet !== this.openSheet ? 'true' : 'false');
        if (mobile) {
          sheet.setAttribute('role', 'dialog');
          sheet.hidden = sheet !== this.openSheet;
        }
        else {
          sheet.hidden = true;
          sheet.setAttribute('aria-hidden', 'true');
          sheet.removeAttribute('role');
          sheet.removeAttribute('aria-modal');
        }
      });
      if (this.backdrop) this.backdrop.hidden = !mobile || !this.openSheet;
    }

    async navigate(url, options = {}) {
      const { push = false, focusResults = false } = options;
      this.controller?.abort();
      this.controller = new AbortController();
      const sequence = ++this.requestSequence;
      this.root.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error(`Collection request failed with ${response.status}`);

        const html = await response.text();
        if (sequence !== this.requestSequence) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const replacement = Array.from(documentFragment.querySelectorAll('[data-bk-collection-root]'))
          .find((element) => element.dataset.sectionId === this.sectionId);
        if (!replacement) throw new Error('Collection section was not present in the response');

        const oldRoot = this.root;
        this.destroy();
        oldRoot.replaceWith(replacement);
        instances.delete(this.sectionId);
        scan(replacement);
        window.BKV293Wishlist?.sync();

        if (documentFragment.title) document.title = documentFragment.title;
        if (push) window.history.pushState({ bkCollection: true }, '', url);

        if (focusResults) {
          const heading = replacement.querySelector('[data-bk-results-heading]');
          heading?.focus({ preventScroll: true });
          replacement.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(url);
      }
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scan(scope = document) {
    const roots = scope.matches?.('[data-bk-collection-root]')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('[data-bk-collection-root]') || []);

    roots.forEach((root) => {
      const sectionId = root.dataset.sectionId;
      const current = instances.get(sectionId);
      if (current?.root === root) return;
      current?.destroy();
      instances.set(sectionId, new BKCollection(root));
    });
  }

  window.addEventListener('popstate', () => {
    instances.forEach((instance) => instance.navigate(window.location.href, { push: false, focusResults: false }));
  });

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, sectionId) => {
      if (event.target === instance.root || event.target.contains(instance.root)) {
        instance.destroy();
        instances.delete(sectionId);
      }
    });
  });

  window.BKCollectionEnhancement = { scan };
  scan();
})();
